import { Flex, HStack, Link, Text } from '@chakra-ui/react';
import { Panel, PanelHeader, PanelHeading } from '@gamma/ui/layout';
import { RouterLink } from '@gamma/ui/navigation';
import LiveHelpSharpIcon from '@gamma/ui/icons/LiveHelpSharpIcon';
import { i18n } from '@investigator/shared/localization';
import { ROUTES } from '@investigator/shared/constants';
import { useEntitiesContext } from '../../context';
import {
  getPivotQuestions,
  getPivotQueryByQuestion,
  substitutePivotQueryParameters,
} from '../../constants/pivotQuestions.generated';

type QuestionUrl = { question: string; url: string };

/**
 * Builds the log search URL for pivot questions
 */
const getPivotQuestionLogSearchQuery = (
  start: number,
  end: number,
  entityName: string,
  question: string,
): string => {
  const pivotQuery = getPivotQueryByQuestion(question);
  if (!pivotQuery) {
    return '';
  }

  // Substitute entity name for HOST_IP or IP_ADDRESS parameters
  const query = substitutePivotQueryParameters(pivotQuery.query, {
    HOST_IP: entityName,
    IP_ADDRESS: entityName,
  });

  const encodedQuery = encodeURIComponent(query);
  return `?query=${encodedQuery}&live=false&humioStart=${start * 1000}&humioEnd=${end * 1000}&fullscreen=false&widgetType=table-view&newestAtBottom=true&showOnlyFirstLine=false&start=${start}&end=${end}`;
};

export const RelatedQuestionsPanel = (): JSX.Element => {
  const { selectedEntity } = useEntitiesContext();
  const modalCopy = i18n.pages.entities.entityAnalysisModal;

  // Use normalized dates from selectedEntity (context handles fallback to global dates)
  const selectedEntityStart = selectedEntity?.start ?? 0;
  const selectedEntityEnd = selectedEntity?.end ?? 0;
  const entityName = selectedEntity?.entityName ?? '';

  // Get static pivot questions
  const questions = getPivotQuestions();

  const questionUrls: QuestionUrl[] = questions.map((question: string) => ({
    question,
    url: getPivotQuestionLogSearchQuery(
      selectedEntityStart,
      selectedEntityEnd,
      entityName,
      question,
    ),
  }));

  return (
    <Panel w="100%">
      <PanelHeader pb={0}>
        <PanelHeading>
          <HStack spacing={2}>
            <LiveHelpSharpIcon boxSize={5} />
            <Text>{modalCopy.relatedQuestions}</Text>
          </HStack>
        </PanelHeading>
      </PanelHeader>
      <Flex gap={2} flexWrap="wrap">
        {questionUrls.map(({ question, url }: QuestionUrl, index: number) => (
          <Link
            key={`question-${index}`}
            as={RouterLink}
            to={{
              pathname: ROUTES.logSearch,
              search: url,
            }}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Search logs for: ${question}`}
            py={1}
            px={2}
            border="1px solid"
            borderColor="whiteAlpha.300"
            borderRadius="100px"
            _hover={{
              borderColor: 'whiteAlpha.500',
              bg: 'whiteAlpha.50',
              textDecoration: 'none',
            }}
            _light={{
              borderColor: 'gray.300',
              _hover: {
                borderColor: 'gray.400',
                bg: 'gray.50',
              },
            }}
            transition="all 0.2s"
          >
            <Text color="text.primary" fontSize="0.8rem" whiteSpace="nowrap">
              {question}
            </Text>
          </Link>
        ))}
      </Flex>
    </Panel>
  );
};
